import React, { FC, useId } from 'react';
import { HoverItemProps } from './HoverItem.types';
import { Stack, Box } from 'src/components-v2/Layout';
import {
  getBorderStyles,
  recursiveMap,
} from 'src/components-v2/Surfaces/AccordionItem/ItemUtils';
import { S } from 'src/components-v2/StyledElements/StyledElements';
import { useDeviceSize } from 'src/hooks';
import AccordionIcons from 'src/components-v2/Surfaces/AccordionItem/AccordionIcons';
import { Image } from 'src/components-v2/Media';
import { useIsDarkBackground } from 'src/contexts';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';

const HoverItem: FC<HoverItemProps> = ({
  title,
  content,
  icon,
  isActive,
  index,
  activeIndex,
  smallImage,
  onClick,
  sys,
  sx,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const accordionId = `accordion-${useId()}`;
  const device = useDeviceSize();
  const isDarkBackground = useIsDarkBackground();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const smImgSource = smallImage?.src;

  const handleMouseOver = () => {
    device.isSmall && setIsHovered(true); // no expansion of div on tablet and desktop
  };

  const handleMouseOut = () => {
    device.isSmall && setIsHovered(false);
  };

  return (
    <S.Li
      sx={{
        ...getBorderStyles(activeIndex, index, isDarkBackground),
        '&:first-child': {
          '& .hoverItem': {
            pt: { xs: '1.6rem', lg: '2.0rem' },
          },
        },
        ...sx,
      }}
      {...inspectorProps({
        fieldId: 'internalTitle',
      })}
    >
      <Box
        onClick={() => {
          return !device.isLarge && onClick(index);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'Space') {
            return !device.isLarge && onClick(index);
          }
        }}
        onMouseOut={handleMouseOut}
        onMouseOver={handleMouseOver}
        onFocus={() => {
          return device.isLarge && onClick(index);
        }}
        onMouseEnter={() => {
          return device.isLarge && onClick(index);
        }}
        className='hoverItem'
        aria-expanded={isActive ? 'true' : 'false'}
        aria-controls={accordionId}
        role='button'
        id={`${accordionId}-control`}
        sx={{
          pt: { xs: '1.6rem', lg: '2.4rem' },
          pb: { xs: '1.6rem', lg: '2.0rem' },
          width: '100%',
          cursor: isActive ? 'initial' : 'pointer',
          '& h4': {
            color: isActive
              ? isDarkBackground
                ? 'white'
                : 'black'
              : isDarkBackground
              ? 'black800'
              : 'black600',
          },
          '& p:nth-last-of-type(1):empty': {
            display: 'none',
          },
          '& img': {
            width: { xs: '40px', lg: '47px' },
            mr: { xs: '0.2rem', sm: '0.0rem', lg: '0.7rem' },
            filter: isActive ? 'none' : 'grayscale(100%)',
            transform: 'translateZ(0)',
          },
        }}
        tabIndex={0}
      >
        <Stack direction='row' gap={1.5} alignItems='center'>
          {smallImage && (
            <Image
              width={47}
              height={47}
              src={smImgSource}
              alt={smallImage?.alt}
              sources={[
                {
                  media: '(max-width: 412px)',
                  srcSet: smImgSource + '?fm=webp&w=80&q=60',
                },
                {
                  media: '(min-width: 413px) and (max-width: 639px)',
                  srcSet: smImgSource + '?fm=webp&w=80&q=60',
                },
                {
                  media: '(min-width: 640px) and (max-width: 1023px)',
                  srcSet: smImgSource + '?fm=webp&w=80&q=60',
                },
                {
                  media: '(min-width: 1024px)',
                  srcSet: smImgSource + '?fm=webp&w=94&q=60',
                },
              ]}
            />
          )}
          <Box sx={{ flex: 1 }}>{title}</Box>
          <AccordionIcons
            sx={{
              color: isActive
                ? isDarkBackground
                  ? 'white'
                  : 'black'
                : isDarkBackground
                ? 'black800'
                : 'black600',
              display: { sm: 'none' },
            }}
            isActive={isActive}
            icon={icon}
          />
        </Stack>
      </Box>
      <Box
        aria-labelledby={`${accordionId}-control`}
        id={accordionId}
        role='region'
        sx={{
          display: 'grid',
          gridTemplateRows: isActive ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.4s cubic-bezier(0.2, 1.2, 0.32, 1)',
          willChange: isHovered ? 'grid-template-rows' : 'auto',
        }}
      >
        <Box
          sx={{
            overflowY: 'hidden',
          }}
        >
          {recursiveMap(content, (child) => {
            if (child?.type === 'a') {
              return React.cloneElement(child, {
                ...child.props,
                tabIndex: isActive ? 0 : -1,
              });
            }
            return child;
          })}
        </Box>
      </Box>
    </S.Li>
  );
};

export default HoverItem;
